// Top toolbar: project, layout, compare mode and its parameters, right-hand B, alignment, zoom, crosshair.

import type { App } from '../app/app';
import type { CompareMode, DiffBand, Layout, SourceId } from '../core/types';
import { B_IDS, ZOOM_LEVELS } from '../core/types';
import { t } from '../i18n';
import { $, $$, segmented, setText } from './dom';
import { pickVideos } from './interactions';
import { pendingAlignments } from './badges';
import { slotLabel } from '../core/types';

const LAYOUTS: Layout[] = ['single', 'side-by-side', 'side-by-side-zoom', 'quad'];
const MODES: CompareMode[] = ['side-by-side', 'wipe', 'overlay', 'flicker', 'difference'];
const LAYOUT_KEY: Record<Layout, string> = {
  'side-by-side': 'layout.sbs',
  'side-by-side-zoom': 'layout.sbs-zoom',
  quad: 'layout.quad',
  focus: 'layout.sbs',
  single: 'layout.single',
};
/** Layout icons (the toolbar is one row, F-18); the names are in the tooltips. */
const rect = (x: number, y: number, w: number, h: number) => `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="1"/>`;
const icon = (...rects: string[]) => `<svg width="18" height="12" viewBox="0 0 18 12" aria-hidden="true">${rects.join('')}</svg>`;
/** A "+" (zoom) drawn inside the cells of the zoom layout. */
const plus = (cx: number, cy: number) => `<path d="M${cx - 1.8} ${cy}h3.6M${cx} ${cy - 1.8}v3.6"/>`;
// Side by side with a "+" in both cells (owner, 2026-10-03).
const ICON_SBS_ZOOM = icon(rect(0.5, 0.5, 8, 11), rect(9.5, 0.5, 8, 11), plus(4.5, 6), plus(13.5, 6));
const LAYOUT_ICON: Record<Layout, string> = {
  'side-by-side': icon(rect(0.5, 0.5, 8, 11), rect(9.5, 0.5, 8, 11)),
  'side-by-side-zoom': ICON_SBS_ZOOM,
  quad: icon(rect(0.5, 0.5, 8, 5), rect(9.5, 0.5, 8, 5), rect(0.5, 6.5, 8, 5), rect(9.5, 6.5, 8, 5)),
  focus: ICON_SBS_ZOOM,
  single: icon(rect(0.5, 0.5, 17, 11)),
};
const MODE_KEY: Record<CompareMode, string> = {
  'side-by-side': 'mode.sbs',
  wipe: 'mode.wipe',
  overlay: 'mode.overlay',
  flicker: 'mode.flicker',
  difference: 'mode.diff',
};

let updLayout: ReturnType<typeof segmented<Layout>>;
let updMode: ReturnType<typeof segmented<CompareMode>>;
let updBand: ReturnType<typeof segmented<DiffBand>>;
let updB: ReturnType<typeof segmented<SourceId>>;
let updZoom: ReturnType<typeof segmented<'fit' | '100'>>;
let updDiff: ReturnType<typeof segmented<'gray' | 'heat'>>;

export function initToolbar(app: App): void {
  updLayout = segmented($('#segLayout'), LAYOUTS, (l) => LAYOUT_ICON[l], (l) => app.setLayout(l), (l) => `${t(LAYOUT_KEY[l])} — ${t(LAYOUT_KEY[l] + '.tip')}`);
  updMode = segmented($('#segMode'), MODES, (m) => t(MODE_KEY[m]), (m) => app.setMode(m), (m) => t(MODE_KEY[m] + '.tip'));
  // Version-vs-version reference (F-05.9).
  $<HTMLSelectElement>('#compareBase').addEventListener('change', (e) => {
    const v = (e.target as HTMLSelectElement).value as SourceId;
    app.store.project((p) => (p.layout.compareBase = v === 'A' ? undefined : v), { undo: false });
  });
  updB = segmented($('#segB'), B_IDS, (b) => slotLabel(b), (b) => app.selectB(b));
  updZoom = segmented($('#segZoom'), ['fit', '100'] as const, (z) => t(z === 'fit' ? 'view.fit' : 'view.actual'), (z) =>
    app.setViewZoom(z === 'fit' ? 'fit' : 100),
  (z) => t(z === 'fit' ? 'view.fitTip' : 'view.actualTip'));
  updDiff = segmented($('#segDiffStyle'), ['gray', 'heat'] as const, (s) => t('params.' + s), (s) =>
    app.store.project((p) => (p.layout.difference.display = s), { undo: false }),
  );
  // F-05.6 / F-05.7: everything, the light-and-colour layer only, or the detail layer only.
  updBand = segmented($('#segDiffBand'), ['all', 'low', 'high'] as const, (b) => t('params.band.' + b), (b) =>
    app.store.project((p) => (p.layout.difference.band = b === 'all' ? undefined : b), { undo: false }),
  (b) => t('params.band.' + b + 'Tip'));
  $('#btnShot').addEventListener('click', () => void app.screenshot());
  $('#btnAddVersion').addEventListener('click', () => {
    const next = B_IDS.find((id) => !app.sources.has(id) && !app.project.sources.find((s) => s.id === id));
    if (next) void pickVideos(app, next);
  });
  $('#btnUndo').addEventListener('click', () => app.store.undo());
  $('#btnRedo').addEventListener('click', () => app.store.redo());
  $('#btnCross').addEventListener('click', () => app.toggleCrosshair());
  const vert = $<HTMLInputElement>('#sbsVertical');
  vert.addEventListener('change', () => app.store.project((p) => (p.layout.sideBySideDirection = vert.checked ? 'vertical' : 'horizontal'), { undo: false }));
  $('#zoomDown').addEventListener('click', () => app.zoomStep(-1));
  $('#zoomUp').addEventListener('click', () => app.zoomStep(1));
  $('#btnZoomDiff').addEventListener('click', () => app.store.project((p) => (p.layout.zoomDifference = !p.layout.zoomDifference), { undo: false }));
  $('#btnZoomGrid').addEventListener('click', () => app.store.project((p) => (p.layout.zoomGrid = !p.layout.zoomGrid), { undo: false }));
  $('#btnAlign').addEventListener('click', () =>
    app.store.ui((u) => {
      u.alignOpen = !u.alignOpen;
      if (!u.alignOpen) u.alignPick = null;
    }),
  );
  const wipe = $<HTMLInputElement>('#wipeAngle');
  wipe.addEventListener('input', () => app.store.project((p) => (p.layout.wipe.angleDeg = Number(wipe.value)), { undo: false }));
  const op = $<HTMLInputElement>('#overlayOpacity');
  op.addEventListener('input', () => app.store.project((p) => (p.layout.overlayOpacity = Number(op.value) / 100), { undo: false }));
  const gain = $<HTMLSelectElement>('#diffGain');
  gain.addEventListener('change', () => app.store.project((p) => (p.layout.difference.gain = Number(gain.value)), { undo: false }));
  const luma = $<HTMLInputElement>('#diffLuma');
  luma.addEventListener('change', () => app.store.project((p) => (p.layout.difference.lumaOnly = luma.checked), { undo: false }));
  // Buttons should not keep keyboard focus: Space and arrows belong to the viewer.
  document.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button');
    if (b) setTimeout(() => b.blur(), 0);
  });
}

export function updateToolbar(app: App): void {
  const st = app.state;
  const L = app.project.layout;
  const oneToOne = app.oneToOne();
  updLayout(L.layout === 'focus' ? 'side-by-side' : L.layout);
  // The quad also has overlay and flicker (F-05.10).
  updMode(L.compareMode, (m) => !app.modeAllowed(m));
  $$('#modeParams .param').forEach((el) => el.classList.toggle('show', app.modeAllowed(L.compareMode) && el.dataset.for === L.compareMode));
  updB(L.selectedB, (b) => !app.sources.has(b));
  $('#segB').style.display = oneToOne ? '' : 'none';
  // "Compare against": for wipe / overlay / flicker / difference once two versions are loaded (F-05.9).
  const others = app.sources.loadedBs().filter((b) => b !== L.selectedB);
  const showBase = oneToOne && L.compareMode !== 'side-by-side' && others.length > 0 && app.sources.has('A');
  $('#compareBaseWrap').hidden = !showBase;
  if (showBase) {
    const sel = $<HTMLSelectElement>('#compareBase');
    const html = ['A', ...others].map((id) => `<option value="${id}">${slotLabel(id as SourceId)}</option>`).join('');
    if (sel.innerHTML !== html) sel.innerHTML = html;
    sel.value = app.view.compareBaseFor(L.selectedB);
  }
  updZoom(L.view.zoomPercent === 'fit' ? 'fit' : '100');
  updDiff(L.difference.display);
  updBand(L.difference.band ?? 'all');
  setText($('#zoomText'), `${L.crosshair.zoomPercent}%`);
  $<HTMLButtonElement>('#zoomDown').disabled = L.crosshair.zoomPercent <= ZOOM_LEVELS[0];
  $<HTMLButtonElement>('#zoomUp').disabled = L.crosshair.zoomPercent >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
  $('#btnCross').classList.toggle('on', L.crosshair.visible);
  $<HTMLInputElement>('#sbsVertical').checked = L.sideBySideDirection === 'vertical';
  // Advanced controls are shown only when switched on in the settings.
  $('#btnZoomDiff').classList.toggle('on', L.zoomDifference);
  $('#btnZoomGrid').classList.toggle('on', L.zoomGrid);
  $('#btnAlign').classList.toggle('on', st.ui.alignOpen);
  $('#btnAlign').classList.toggle('has-suggest', pendingAlignments(app).length > 0);
  $<HTMLButtonElement>('#btnAlign').disabled = !app.sources.has('A') || !app.sources.loadedBs().length;
  $<HTMLButtonElement>('#btnAddVersion').disabled = B_IDS.every((id) => app.sources.has(id) || !!app.project.sources.find((s) => s.id === id));
  $<HTMLButtonElement>('#btnUndo').disabled = !app.store.canUndo;
  $<HTMLButtonElement>('#btnRedo').disabled = !app.store.canRedo;
  const wipe = $<HTMLInputElement>('#wipeAngle');
  wipe.value = String(L.wipe.angleDeg);
  setText($('#wipeAngleOut'), `${L.wipe.angleDeg}°`);
  $<HTMLInputElement>('#overlayOpacity').value = String(Math.round(L.overlayOpacity * 100));
  setText($('#overlayOut'), `${Math.round(L.overlayOpacity * 100)}%`);
  setText($('#flickerInfo'), t('params.flickerInfo', { hz: st.settings.flickerHz }));
  $<HTMLSelectElement>('#diffGain').value = String(L.difference.gain);
  $<HTMLInputElement>('#diffLuma').checked = L.difference.lumaOnly;
  $('#splitter').classList.toggle('show', L.layout === 'side-by-side-zoom' && !st.ui.tempFull);
  if (L.layout === 'side-by-side-zoom') {
    const cv = app.view.canvas;
    const k = cv.width ? cv.getBoundingClientRect().width / cv.width : 1;
    $('#splitter').style.top = `${app.view.topBottom().top.h * k}px`;
  }
}
